/**
 * Browser-storage key prefixes whose values belong to the signed-in user.
 *
 * Anything stored under one of these is personal data for whoever was logged in when it
 * was written, so it must not survive that user's session on a shared browser.
 *
 * Prefixes, not exact keys: the deposit flow scopes its contact block per user id
 * (`asset_deposit_contact:<userId>`) and its media mirror per draft id
 * (`asset_deposit_media_<draftId>`), so the whole family has to go.
 */
export const USER_SCOPED_STORAGE_PREFIXES: readonly string[] = [
    'asset_deposit_contact',
    'asset_deposit_media_'
];

/**
 * Drop every local/session-storage entry belonging to the user whose session is ending.
 *
 * These keys are convenience caches (deposit contact prefill, uploaded-photo mirror), so
 * they carry no auth material and the legacy-auth sweep in WebStorageService never matched
 * them — but they do carry personal data, and on a shared browser the next user must not
 * see it.
 *
 * A free function rather than a method so `UserStateService` — the one chokepoint every
 * auth reset reaches, including 401 session-expiry — can call it without depending on
 * `WebStorageService`, which already depends on it.
 */
export function clearUserScopedStorage(): void {
    sweepByPrefix(localStorage);
    sweepByPrefix(sessionStorage);
}

function sweepByPrefix(store: Storage): void {
    try {
        const keysToRemove: string[] = [];

        for (let i = 0; i < store.length; i += 1) {
            const key = store.key(i);
            if (key && USER_SCOPED_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix))) {
                keysToRemove.push(key);
            }
        }

        keysToRemove.forEach((key) => store.removeItem(key));
    } catch {
        // Storage may be unavailable (private mode, blocked) — logout must still proceed.
    }
}
